import { Button } from "../ui/button";
import { Play, Pause, SkipBack, SkipForward, X, Volume2 } from "lucide-react";
import { formatDuration } from "./utils";
import type { AudioPlayerProps } from "./types";
import { useSidebar } from "../ui/sidebar";
import { useIsMobile } from "@/hooks/use-mobile";

export const AudioPlayer = ({
  currentPlayingUrl,
  currentPlayingRecording,
  recordings,
  selectedUser,
  selectedSession,
  isPlaying,
  currentTime,
  duration,
  audioRef,
  onPlayPause,
  onClose,
  onSeek,
  onPrevious,
  onNext,
  onTimeUpdate,
  onLoadedMetadata,
  onEnded,
  onPlay,
  onPauseEvent,
  getUserDisplayName,
}: AudioPlayerProps) => {
  const { state } = useSidebar();
  const isMobile = useIsMobile();

  if (!currentPlayingUrl) return null;

  const currentIndex = currentPlayingRecording
    ? recordings.findIndex((r) => r.key === currentPlayingRecording.key)
    : -1;

  const sidebarWidth = isMobile
    ? 0
    : state === "collapsed"
      ? "var(--sidebar-width-icon)"
      : "var(--sidebar-width)";

  return (
    <div
      className="fixed bottom-0 left-0 right-0 bg-card border-t z-50 transition-[left] duration-200 ease-linear"
      style={{ left: sidebarWidth }}
    >
      {/* Progress bar accent */}
      <div className="absolute top-0 left-0 right-0 h-px bg-muted">
        <div
          className="h-full bg-primary transition-all duration-100"
          style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
        />
      </div>

      <div className="flex items-center justify-between p-3 px-4 gap-4">
        {/* Track Info */}
        <div className="flex items-center gap-3 flex-1 min-w-0 max-w-[300px]">
          <div className="size-9 rounded-lg bg-muted flex items-center justify-center shrink-0">
            <Volume2 className="size-3.5 text-muted-foreground" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-medium truncate">
              Recording {currentIndex + 1} of {recordings.length}
            </div>
            <div className="text-xs text-muted-foreground truncate">
              {selectedUser && getUserDisplayName(selectedUser.user_id)}
              {selectedSession?.lesson_name &&
                ` • ${selectedSession.lesson_name}`}
            </div>
          </div>
        </div>

        {/* Player Controls */}
        <div className="flex-1 flex flex-col gap-2 max-w-[600px]">
          <div className="flex items-center justify-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              className="size-9 text-muted-foreground hover:text-foreground"
              onClick={onPrevious}
              disabled={currentIndex <= 0}
              title="Previous"
            >
              <SkipBack className="size-4" />
            </Button>

            <Button
              size="icon"
              className="size-10 rounded-full"
              onClick={onPlayPause}
              title={isPlaying ? "Pause" : "Play"}
            >
              {isPlaying ? (
                <Pause className="size-4" />
              ) : (
                <Play className="size-4 ml-0.5" />
              )}
            </Button>

            <Button
              variant="ghost"
              size="icon"
              className="size-9 text-muted-foreground hover:text-foreground"
              onClick={onNext}
              disabled={
                currentIndex === -1 || currentIndex >= recordings.length - 1
              }
              title="Next"
            >
              <SkipForward className="size-4" />
            </Button>
          </div>

          <div className="w-full">
            <audio
              ref={audioRef}
              autoPlay
              src={currentPlayingUrl}
              onPlay={onPlay}
              onPause={onPauseEvent}
              onEnded={onEnded}
              onTimeUpdate={onTimeUpdate}
              onLoadedMetadata={onLoadedMetadata}
              className="hidden"
            />
            <div className="flex items-center gap-3 w-full">
              <span className="text-xs text-muted-foreground font-mono w-10 text-right">
                {formatDuration(currentTime)}
              </span>
              <div
                className="flex-1 h-1 bg-muted rounded-full overflow-hidden cursor-pointer relative group hover:h-1.5 transition-all"
                onClick={onSeek}
              >
                <div
                  className="h-full bg-primary rounded-full"
                  style={{
                    width: `${duration ? (currentTime / duration) * 100 : 0}%`,
                  }}
                />
              </div>
              <span className="text-xs text-muted-foreground font-mono w-10">
                {formatDuration(duration)}
              </span>
            </div>
          </div>
        </div>

        {/* Close Button */}
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="size-9 text-muted-foreground hover:text-foreground"
            onClick={onClose}
            title="Close player"
          >
            <X className="size-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};
